import React, { useState, useEffect } from 'react';
import { Search, Filter, Eye, Edit, Trash2, Users, TrendingUp, FileText, DollarSign, ChevronDown, X, Save, Download } from 'lucide-react';
import axios from 'axios';
import config from '../config/config';
import html2pdf from 'html2pdf.js'; // Import html2pdf.js

export default function SponsorshipDashboard() {
  const [sponsorships, setSponsorships] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  // Statistics derived from fetched data
  const stats = {
    total: sponsorships.length,
    active: sponsorships.filter(s => s.status === 'submitted').length,
    pending: sponsorships.filter(s => s.status === 'draft').length,
    review: sponsorships.filter(s => s.status === 'review').length,
  };

  // Fetch sponsorship applications
  useEffect(() => {
    const fetchSponsorships = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        await axios.get(`${config.API_URL}/sanctum/csrf-cookie`, { withCredentials: true });
        const xsrfToken = getCookie('XSRF-TOKEN');
        if (!xsrfToken) throw new Error('CSRF token not found');

        const response = await axios.get(`${config.API_URL}/api/sponsorship-applications-data`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-XSRF-TOKEN': xsrfToken,
          },
          withCredentials: true,
        });

        setSponsorships(response.data);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch sponsorship applications. Please try again.');
        setLoading(false);
        console.error('Fetch error:', err);
      }
    };

    fetchSponsorships();
  }, []);

  // Utility function to get cookie
  const getCookie = (name) => {
    const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    if (match) return decodeURIComponent(match[2]);
    return null;
  };

  // Validation rules
  const validateField = (field, value) => {
    switch (field) {
      case 'projectName':
      case 'name':
      case 'class':
      case 'healthStatus':
      case 'hobbiesAndLikes':
      case 'areaOfResidence':
      case 'sourceOfIncome':
      case 'houseCondition':
      case 'mainReasons':
        return value.trim() ? '' : 'This field is required';
      case 'phoneNo':
        return /^[0-9+\-() ]{7,20}$/.test(value) ? '' : 'Invalid phone number';
      case 'dob':
        return value && new Date(value) <= new Date() ? '' : 'Invalid or future date';
      case 'motherAge':
      case 'fatherAge':
      case 'guardianAge':
        return !value || (Number(value) > 0 && Number(value) <= 120) ? '' : 'Age must be between 1 and 120';
      case 'siblings':
        return value.every(sibling => 
          sibling.name.trim() && 
          Number(sibling.age) > 0 && Number(sibling.age) <= 120 && 
          sibling.class.trim()
        ) ? '' : 'All sibling fields are required and age must be between 1 and 120';
      default:
        return '';
    }
  };

  // Real-time validation
  useEffect(() => {
    if (isEditing && formData) {
      const newErrors = {};
      Object.keys(formData).forEach(field => {
        if (field === 'siblings') {
          newErrors[field] = validateField(field, formData.siblings);
        } else if (!['motherName', 'motherOccupation', 'motherHealthStatus', 'fatherName', 'fatherOccupation', 'fatherHealthStatus', 'guardianName', 'guardianOccupation', 'newFamiliesInfo', 'otherFamilyMembers'].includes(field)) {
          newErrors[field] = validateField(field, formData[field]);
        }
      });
      setFormErrors(newErrors);
    }
  }, [formData, isEditing]);

  // Initialize form data when editing
  const handleEdit = (record) => {
    const familyMembers = {
      motherName: '',
      motherAge: '',
      motherOccupation: '',
      motherHealthStatus: '',
      fatherName: '',
      fatherAge: '',
      fatherOccupation: '',
      fatherHealthStatus: '',
      guardianName: '',
      guardianAge: '',
      guardianOccupation: '',
    };
    record.family_members?.forEach(member => {
      if (member.type === 'mother') {
        familyMembers.motherName = member.name || '';
        familyMembers.motherAge = member.age || '';
        familyMembers.motherOccupation = member.occupation || '';
        familyMembers.motherHealthStatus = member.health_status || '';
      } else if (member.type === 'father') {
        familyMembers.fatherName = member.name || '';
        familyMembers.fatherAge = member.age || '';
        familyMembers.fatherOccupation = member.occupation || '';
        familyMembers.fatherHealthStatus = member.health_status || '';
      } else if (member.type === 'guardian') {
        familyMembers.guardianName = member.name || '';
        familyMembers.guardianAge = member.age || '';
        familyMembers.guardianOccupation = member.occupation || '';
      }
    });

    setFormData({
      projectName: record.project_name,
      phoneNo: record.phone_no,
      name: record.name,
      dob: record.dob,
      class: record.class,
      healthStatus: record.health_status,
      hobbiesAndLikes: record.hobbies_and_likes,
      areaOfResidence: record.area_of_residence,
      ...familyMembers,
      newFamiliesInfo: record.new_families_info || '',
      sourceOfIncome: record.source_of_income,
      houseCondition: record.house_condition,
      mainReasons: record.main_reasons,
      status: record.status,
      siblings: record.siblings || [{ name: '', age: '', class: '' }],
      otherFamilyMembers: record.other_family_members?.map(m => m.name) || ['', '', ''],
    });
    setSelectedRecord(record);
    setIsEditing(true);
    setShowModal(true);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSiblingChange = (index, field, value) => {
    const newSiblings = [...formData.siblings];
    newSiblings[index] = { ...newSiblings[index], [field]: value };
    setFormData(prev => ({ ...prev, siblings: newSiblings }));
  };

  const addSibling = () => {
    setFormData(prev => ({
      ...prev,
      siblings: [...prev.siblings, { name: '', age: '', class: '' }],
    }));
  };

  const removeSibling = (index) => {
    if (formData.siblings.length > 1) {
      setFormData(prev => ({
        ...prev,
        siblings: prev.siblings.filter((_, i) => i !== index),
      }));
    } else {
      setFormErrors(prev => ({ ...prev, siblings: 'At least one sibling entry is required' }));
    }
  };

  const handleOtherFamilyChange = (index, value) => {
    const newMembers = [...formData.otherFamilyMembers];
    newMembers[index] = value;
    setFormData(prev => ({ ...prev, otherFamilyMembers: newMembers }));
  };

  const handleSaveEdit = async () => {
    const hasErrors = Object.values(formErrors).some(error => error);
    if (hasErrors) {
      setError('Please fix all validation errors before saving');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.get(`${config.API_URL}/sanctum/csrf-cookie`, { withCredentials: true });
      const xsrfToken = getCookie('XSRF-TOKEN');
      if (!xsrfToken) throw new Error('CSRF token not found');

      const payload = {
        projectName: formData.projectName,
        phoneNo: formData.phoneNo,
        name: formData.name,
        dob: formData.dob,
        class: formData.class,
        healthStatus: formData.healthStatus,
        hobbiesAndLikes: formData.hobbiesAndLikes,
        areaOfResidence: formData.areaOfResidence,
        motherName: formData.motherName,
        motherAge: formData.motherAge,
        motherOccupation: formData.motherOccupation,
        motherHealthStatus: formData.motherHealthStatus,
        fatherName: formData.fatherName,
        fatherAge: formData.fatherAge,
        fatherOccupation: formData.fatherOccupation,
        fatherHealthStatus: formData.fatherHealthStatus,
        guardianName: formData.guardianName,
        guardianAge: formData.guardianAge,
        guardianOccupation: formData.guardianOccupation,
        newFamiliesInfo: formData.newFamiliesInfo,
        sourceOfIncome: formData.sourceOfIncome,
        houseCondition: formData.houseCondition,
        mainReasons: formData.mainReasons,
        status: formData.status,
        siblings: formData.siblings,
        otherFamilyMembers: formData.otherFamilyMembers,
      };

      const response = await axios.post(`${config.API_URL}/api/updateSurvey/${selectedRecord.id}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-XSRF-TOKEN': xsrfToken,
        },
        withCredentials: true,
      });

      setSponsorships(sponsorships.map(s => s.id === selectedRecord.id ? response.data : s));
      setIsEditing(false);
      setShowModal(false);
      setError(null);
      alert('Application updated successfully!');
    } catch (err) {
      setError('Failed to update application. Please try again.');
      console.error('Update error:', err);
    }
  };

  const handleViewDetails = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.get(`${config.API_URL}/sanctum/csrf-cookie`, { withCredentials: true });
      const xsrfToken = getCookie('XSRF-TOKEN');
      if (!xsrfToken) throw new Error('CSRF token not found');

      const response = await axios.get(`${config.API_URL}/api/sponsorship-applications-data-view/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-XSRF-TOKEN': xsrfToken,
        },
        withCredentials: true,
      });

      setSelectedRecord(response.data);
      setIsEditing(false);
      setShowModal(true);
    } catch (err) {
      setError('Failed to fetch application details. Please try again.');
      console.error('Fetch details error:', err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this record?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.get(`${config.API_URL}/sanctum/csrf-cookie`, { withCredentials: true });
        const xsrfToken = getCookie('XSRF-TOKEN');
        if (!xsrfToken) throw new Error('CSRF token not found');

        await axios.delete(`${config.API_URL}/api/delete-sponsorship-applications/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-XSRF-TOKEN': xsrfToken,
          },
          withCredentials: true,
        });

        setSponsorships(sponsorships.filter(s => s.id !== id));
      } catch (err) {
        setError('Failed to delete application. Please try again.');
        console.error('Delete error:', err);
      }
    }
  };

  // New function to handle PDF export
  const handleExportPDF = () => {
    const element = document.getElementById('pdf-content');
    if (!element) return;

    const opt = {
      margin: [10, 10, 10, 10], // Compact margins
      filename: `sponsorship_application_${selectedRecord.name.replace(/\s+/g, '_')}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    };

    html2pdf().set(opt).from(element).save().catch(err => {
      console.error('PDF export error:', err);
      setError('Failed to export PDF. Please try again.');
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'review':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredData = sponsorships.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          item.project_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          item.area_of_residence.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'All' || item.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const calculateAge = (dob) => {
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Removed Export Data button */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Sponsorship Dashboard</h1>
              <p className="text-gray-600 mt-1">Manage and monitor all sponsorship applications</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Error Message */}
        {error && <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg">{error}</div>}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">Loading applications...</p>
          </div>
        )}

        {/* Statistics Cards */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">Total Applications</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stats.total}</p>
                </div>
                <div className="bg-blue-100 p-4 rounded-lg">
                  <FileText size={24} className="text-blue-600" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">Submitted Applications</p>
                  <p className="text-3xl font-bold text-green-600 mt-2">{stats.active}</p>
                </div>
                <div className="bg-green-100 p-4 rounded-lg">
                  <Users size={24} className="text-green-600" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">Draft Applications</p>
                  <p className="text-3xl font-bold text-yellow-600 mt-2">{stats.pending}</p>
                </div>
                <div className="bg-yellow-100 p-4 rounded-lg">
                  <TrendingUp size={24} className="text-yellow-600" />
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">Under Review</p>
                  <p className="text-3xl font-bold text-blue-600 mt-2">{stats.review}</p>
                </div>
                <div className="bg-blue-100 p-4 rounded-lg">
                  <DollarSign size={24} className="text-blue-600" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filter Bar */}
        {!loading && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-200">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search by name, project, or area..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="relative">
                <Filter className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="pl-12 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white min-w-[200px]"
                >
                  <option value="All">All Status</option>
                  <option value="submitted">Submitted</option>
                  <option value="draft">Draft</option>
                  <option value="review">Review</option>
                </select>
                <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              </div>
            </div>
          </div>
        )}

        {/* Data Table */}
        {!loading && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Project</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Age/Class</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Area</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Siblings</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredData.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">{record.name}</div>
                        <div className="text-sm text-gray-500">{record.phone_no}</div>
                      </td>
                      <td className="px-6 py-4 text-gray-700">{record.project_name}</td>
                      <td className="px-6 py-4">
                        <div className="text-gray-900">{calculateAge(record.dob)} years</div>
                        <div className="text-sm text-gray-500">{record.class}</div>
                      </td>
                      <td className="px-6 py-4 text-gray-700">{record.area_of_residence}</td>
                      <td className="px-6 py-4 text-gray-700 text-center">{record.siblings?.length || 0}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(record.status)}`}>
                          {record.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-700 text-sm">{record.created_at.split('T')[0]}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleViewDetails(record.id)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                            title="View Details"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            onClick={() => handleEdit(record)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition"
                            title="Edit"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(record.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredData.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No records found</p>
                <p className="text-gray-400 text-sm mt-2">Try adjusting your search or filter criteria</p>
              </div>
            )}
          </div>
        )}

        {/* Hidden PDF Content Container */}
        {selectedRecord && (
          <div id="pdf-content" className="hidden">
            <div className="p-4 font-sans" style={{ fontSize: '12px', lineHeight: '1.5' }}>
              <h1 className="text-2xl font-bold mb-4">Sponsorship Application: {selectedRecord.name}</h1>
              
              <div className="mb-4">
                <h2 className="text-lg font-semibold border-b border-gray-300 pb-1">Basic Information</h2>
                <p><strong>Name:</strong> {selectedRecord.name}</p>
                <p><strong>Project:</strong> {selectedRecord.project_name}</p>
                <p><strong>Date of Birth:</strong> {selectedRecord.dob}</p>
                <p><strong>Age:</strong> {calculateAge(selectedRecord.dob)} years</p>
                <p><strong>Class:</strong> {selectedRecord.class}</p>
                <p><strong>Phone Number:</strong> {selectedRecord.phone_no}</p>
                <p><strong>Health Status:</strong> {selectedRecord.health_status}</p>
                <p><strong>Area of Residence:</strong> {selectedRecord.area_of_residence}</p>
                <p><strong>Hobbies and Likes:</strong> {selectedRecord.hobbies_and_likes}</p>
              </div>

              <div className="mb-4">
                <h2 className="text-lg font-semibold border-b border-gray-300 pb-1">Family Information</h2>
                {selectedRecord.family_members?.map((member, index) => (
                  <div key={index} className="mb-2">
                    <p><strong>{member.type.charAt(0).toUpperCase() + member.type.slice(1)}'s Name:</strong> {member.name || 'N/A'}</p>
                    <p><strong>Age:</strong> {member.age || 'N/A'}</p>
                    <p><strong>Occupation:</strong> {member.occupation || 'N/A'}</p>
                    <p><strong>Health Status:</strong> {member.health_status || 'N/A'}</p>
                  </div>
                ))}
                <p><strong>Number of Siblings:</strong> {selectedRecord.siblings?.length || 0}</p>
                {selectedRecord.siblings?.length > 0 && (
                  <div>
                    <p><strong>Siblings:</strong></p>
                    <ul className="list-disc pl-5">
                      {selectedRecord.siblings.map((sibling, index) => (
                        <li key={index}>{sibling.name} (Age: {sibling.age}, Class: {sibling.class})</li>
                      ))}
                    </ul>
                  </div>
                )}
                {selectedRecord.other_family_members?.length > 0 && (
                  <div>
                    <p><strong>Other Family Members:</strong></p>
                    <ul className="list-disc pl-5">
                      {selectedRecord.other_family_members.map((member, index) => (
                        <li key={index}>{member.name}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <p><strong>New Families Info:</strong> {selectedRecord.new_families_info || 'N/A'}</p>
                <p><strong>Source of Income:</strong> {selectedRecord.source_of_income}</p>
              </div>

              <div className="mb-4">
                <h2 className="text-lg font-semibold border-b border-gray-300 pb-1">Application Status</h2>
                <p><strong>Current Status:</strong> {selectedRecord.status}</p>
                <p><strong>Submitted Date:</strong> {selectedRecord.created_at.split('T')[0]}</p>
              </div>

              <div className="mb-4">
                <h2 className="text-lg font-semibold border-b border-gray-300 pb-1">Economic and Housing</h2>
                <p><strong>House Condition:</strong> {selectedRecord.house_condition}</p>
                <p><strong>Main Reasons for Sponsorship:</strong> {selectedRecord.main_reasons}</p>
              </div>
            </div>
          </div>
        )}

        {/* Details/Edit Modal */}
        {showModal && selectedRecord && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">{isEditing ? 'Edit Sponsorship Application' : 'Sponsorship Details'}</h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {isEditing ? (
                  <>
                    {/* Edit Form */}
                    <div className="space-y-6">
                      {/* Basic Information */}
                      <div className="bg-blue-50 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Project Name</label>
                            <input
                              type="text"
                              value={formData.projectName}
                              onChange={(e) => handleInputChange('projectName', e.target.value)}
                              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${formErrors.projectName ? 'border-red-500' : 'border-gray-300'}`}
                              placeholder="Enter project name"
                            />
                            {formErrors.projectName && <p className="text-red-500 text-sm mt-1">{formErrors.projectName}</p>}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Phone No</label>
                            <input
                              type="tel"
                              value={formData.phoneNo}
                              onChange={(e) => handleInputChange('phoneNo', e.target.value)}
                              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${formErrors.phoneNo ? 'border-red-500' : 'border-gray-300'}`}
                              placeholder="Enter phone number"
                            />
                            {formErrors.phoneNo && <p className="text-red-500 text-sm mt-1">{formErrors.phoneNo}</p>}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                            <input
                              type="text"
                              value={formData.name}
                              onChange={(e) => handleInputChange('name', e.target.value)}
                              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${formErrors.name ? 'border-red-500' : 'border-gray-300'}`}
                              placeholder="Enter full name"
                            />
                            {formErrors.name && <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                            <input
                              type="date"
                              value={formData.dob}
                              onChange={(e) => handleInputChange('dob', e.target.value)}
                              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${formErrors.dob ? 'border-red-500' : 'border-gray-300'}`}
                            />
                            {formErrors.dob && <p className="text-red-500 text-sm mt-1">{formErrors.dob}</p>}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Class</label>
                            <input
                              type="text"
                              value={formData.class}
                              onChange={(e) => handleInputChange('class', e.target.value)}
                              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${formErrors.class ? 'border-red-500' : 'border-gray-300'}`}
                              placeholder="Enter class"
                            />
                            {formErrors.class && <p className="text-red-500 text-sm mt-1">{formErrors.class}</p>}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Health Status</label>
                            <input
                              type="text"
                              value={formData.healthStatus}
                              onChange={(e) => handleInputChange('healthStatus', e.target.value)}
                              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${formErrors.healthStatus ? 'border-red-500' : 'border-gray-300'}`}
                              placeholder="Enter health status"
                            />
                            {formErrors.healthStatus && <p className="text-red-500 text-sm mt-1">{formErrors.healthStatus}</p>}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Hobbies and Likes</label>
                            <textarea
                              value={formData.hobbiesAndLikes}
                              onChange={(e) => handleInputChange('hobbiesAndLikes', e.target.value)}
                              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${formErrors.hobbiesAndLikes ? 'border-red-500' : 'border-gray-300'}`}
                              rows="3"
                              placeholder="Enter hobbies and interests"
                            />
                            {formErrors.hobbiesAndLikes && <p className="text-red-500 text-sm mt-1">{formErrors.hobbiesAndLikes}</p>}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Area of Residence</label>
                            <input
                              type="text"
                              value={formData.areaOfResidence}
                              onChange={(e) => handleInputChange('areaOfResidence', e.target.value)}
                              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${formErrors.areaOfResidence ? 'border-red-500' : 'border-gray-300'}`}
                              placeholder="Enter area of residence"
                            />
                            {formErrors.areaOfResidence && <p className="text-red-500 text-sm mt-1">{formErrors.areaOfResidence}</p>}
                          </div>
                        </div>
                      </div>

                      {/* Family Information */}
                      <div className="bg-green-50 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Family Information</h3>
                        <div className="space-y-4">
                          <div>
                            <h4 className="text-md font-medium text-gray-800 mb-2">Mother's Information</h4>
                            <input
                              type="text"
                              value={formData.motherName}
                              onChange={(e) => handleInputChange('motherName', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white"
                              placeholder="Mother's name"
                            />
                            <div className="grid grid-cols-2 gap-4 mt-2">
                              <div>
                                <input
                                  type="number"
                                  value={formData.motherAge}
                                  onChange={(e) => handleInputChange('motherAge', e.target.value)}
                                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white ${formErrors.motherAge ? 'border-red-500' : 'border-gray-300'}`}
                                  placeholder="Age"
                                />
                                {formErrors.motherAge && <p className="text-red-500 text-sm mt-1">{formErrors.motherAge}</p>}
                              </div>
                              <input
                                type="text"
                                value={formData.motherOccupation}
                                onChange={(e) => handleInputChange('motherOccupation', e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white"
                                placeholder="Occupation"
                              />
                            </div>
                            <input
                              type="text"
                              value={formData.motherHealthStatus}
                              onChange={(e) => handleInputChange('motherHealthStatus', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white mt-2"
                              placeholder="Health status"
                            />
                          </div>
                          <div>
                            <h4 className="text-md font-medium text-gray-800 mb-2">Father's Information</h4>
                            <input
                              type="text"
                              value={formData.fatherName}
                              onChange={(e) => handleInputChange('fatherName', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white"
                              placeholder="Father's name"
                            />
                            <div className="grid grid-cols-2 gap-4 mt-2">
                              <div>
                                <input
                                  type="number"
                                  value={formData.fatherAge}
                                  onChange={(e) => handleInputChange('fatherAge', e.target.value)}
                                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white ${formErrors.fatherAge ? 'border-red-500' : 'border-gray-300'}`}
                                  placeholder="Age"
                                />
                                {formErrors.fatherAge && <p className="text-red-500 text-sm mt-1">{formErrors.fatherAge}</p>}
                              </div>
                              <input
                                type="text"
                                value={formData.fatherOccupation}
                                onChange={(e) => handleInputChange('fatherOccupation', e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white"
                                placeholder="Occupation"
                              />
                            </div>
                            <input
                              type="text"
                              value={formData.fatherHealthStatus}
                              onChange={(e) => handleInputChange('fatherHealthStatus', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white mt-2"
                              placeholder="Health status"
                            />
                          </div>
                          <div>
                            <h4 className="text-md font-medium text-gray-800 mb-2">Guardian's Information</h4>
                            <input
                              type="text"
                              value={formData.guardianName}
                              onChange={(e) => handleInputChange('guardianName', e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white"
                              placeholder="Guardian's name"
                            />
                            <div className="grid grid-cols-2 gap-4 mt-2">
                              <div>
                                <input
                                  type="number"
                                  value={formData.guardianAge}
                                  onChange={(e) => handleInputChange('guardianAge', e.target.value)}
                                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white ${formErrors.guardianAge ? 'border-red-500' : 'border-gray-300'}`}
                                  placeholder="Age"
                                />
                                {formErrors.guardianAge && <p className="text-red-500 text-sm mt-1">{formErrors.guardianAge}</p>}
                              </div>
                              <input
                                type="text"
                                value={formData.guardianOccupation}
                                onChange={(e) => handleInputChange('guardianOccupation', e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white"
                                placeholder="Occupation"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Siblings & Other Members */}
                      <div className="bg-purple-50 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Siblings & Other Members</h3>
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="text-md font-medium text-gray-800">Siblings</h4>
                          <button
                            onClick={addSibling}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
                          >
                            + Add Sibling
                          </button>
                        </div>
                        {formErrors.siblings && <p className="text-red-500 text-sm mb-2">{formErrors.siblings}</p>}
                        <div className="space-y-4">
                          {formData.siblings.map((sibling, index) => (
                            <div key={index} className="grid grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg items-center">
                              <input
                                type="text"
                                value={sibling.name}
                                onChange={(e) => handleSiblingChange(index, 'name', e.target.value)}
                                className={`px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white ${!sibling.name.trim() && formErrors.siblings ? 'border-red-500' : 'border-gray-300'}`}
                                placeholder="Name"
                              />
                              <input
                                type="number"
                                value={sibling.age}
                                onChange={(e) => handleSiblingChange(index, 'age', e.target.value)}
                                className={`px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white ${(!sibling.age || Number(sibling.age) <= 0 || Number(sibling.age) > 120) && formErrors.siblings ? 'border-red-500' : 'border-gray-300'}`}
                                placeholder="Age"
                              />
                              <input
                                type="text"
                                value={sibling.class}
                                onChange={(e) => handleSiblingChange(index, 'class', e.target.value)}
                                className={`px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition bg-white ${!sibling.class.trim() && formErrors.siblings ? 'border-red-500' : 'border-gray-300'}`}
                                placeholder="Class"
                              />
                              <button
                                onClick={() => removeSibling(index)}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm flex items-center gap-2"
                                disabled={formData.siblings.length === 1}
                              >
                                <Trash2 size={16} />
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                        <div className="mt-4">
                          <h4 className="text-md font-medium text-gray-800 mb-2">Other Family Members</h4>
                          {formData.otherFamilyMembers.map((member, index) => (
                            <input
                              key={index}
                              type="text"
                              value={member}
                              onChange={(e) => handleOtherFamilyChange(index, e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition mt-2"
                              placeholder={`Family member ${index + 1}`}
                            />
                          ))}
                        </div>
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">New Families Info</label>
                          <textarea
                            value={formData.newFamiliesInfo}
                            onChange={(e) => handleInputChange('newFamiliesInfo', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                            rows="3"
                            placeholder="Enter information about new families"
                          />
                        </div>
                      </div>

                      {/* Economic and Housing */}
                      <div className="bg-amber-50 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Economic and Housing</h3>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Source of Income</label>
                            <textarea
                              value={formData.sourceOfIncome}
                              onChange={(e) => handleInputChange('sourceOfIncome', e.target.value)}
                              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${formErrors.sourceOfIncome ? 'border-red-500' : 'border-gray-300'}`}
                              rows="3"
                              placeholder="Describe the family's source of income"
                            />
                            {formErrors.sourceOfIncome && <p className="text-red-500 text-sm mt-1">{formErrors.sourceOfIncome}</p>}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">House Condition</label>
                            <textarea
                              value={formData.houseCondition}
                              onChange={(e) => handleInputChange('houseCondition', e.target.value)}
                              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${formErrors.houseCondition ? 'border-red-500' : 'border-gray-300'}`}
                              rows="3"
                              placeholder="Describe the house condition"
                            />
                            {formErrors.houseCondition && <p className="text-red-500 text-sm mt-1">{formErrors.houseCondition}</p>}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Main Reasons for Sponsorship</label>
                            <textarea
                              value={formData.mainReasons}
                              onChange={(e) => handleInputChange('mainReasons', e.target.value)}
                              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${formErrors.mainReasons ? 'border-red-500' : 'border-gray-300'}`}
                              rows="5"
                              placeholder="Describe the main reasons for sponsorship"
                            />
                            {formErrors.mainReasons && <p className="text-red-500 text-sm mt-1">{formErrors.mainReasons}</p>}
                          </div>
                        </div>
                      </div>

                      {/* Status */}
                      <div className="bg-purple-50 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Application Status</h3>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                          <select
                            value={formData.status}
                            onChange={(e) => handleInputChange('status', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                          >
                            <option value="draft">Draft</option>
                            <option value="submitted">Submitted</option>
                            <option value="review">Review</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* View Mode */}
                    <div className="bg-blue-50 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600">Name</p>
                          <p className="font-medium text-gray-900">{selectedRecord.name}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Project</p>
                          <p className="font-medium text-gray-900">{selectedRecord.project_name}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Date of Birth</p>
                          <p className="font-medium text-gray-900">{selectedRecord.dob}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Age</p>
                          <p className="font-medium text-gray-900">{calculateAge(selectedRecord.dob)} years</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Class</p>
                          <p className="font-medium text-gray-900">{selectedRecord.class}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Phone Number</p>
                          <p className="font-medium text-gray-900">{selectedRecord.phone_no}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Health Status</p>
                          <p className="font-medium text-gray-900">{selectedRecord.health_status}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Area of Residence</p>
                          <p className="font-medium text-gray-900">{selectedRecord.area_of_residence}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Hobbies and Likes</p>
                          <p className="font-medium text-gray-900">{selectedRecord.hobbies_and_likes}</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-50 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Family Information</h3>
                      <div className="grid grid-cols-2 gap-4">
                        {selectedRecord.family_members?.map((member, index) => (
                          <div key={index}>
                            <p className="text-sm text-gray-600">{member.type.charAt(0).toUpperCase() + member.type.slice(1)}'s Name</p>
                            <p className="font-medium text-gray-900">{member.name || 'N/A'}</p>
                            <p className="text-sm text-gray-600">Age</p>
                            <p className="font-medium text-gray-900">{member.age || 'N/A'}</p>
                            <p className="text-sm text-gray-600">Occupation</p>
                            <p className="font-medium text-gray-900">{member.occupation || 'N/A'}</p>
                            <p className="text-sm text-gray-600">Health Status</p>
                            <p className="font-medium text-gray-900">{member.health_status || 'N/A'}</p>
                          </div>
                        ))}
                        <div>
                          <p className="text-sm text-gray-600">Number of Siblings</p>
                          <p className="font-medium text-gray-900">{selectedRecord.siblings?.length || 0}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Source of Income</p>
                          <p className="font-medium text-gray-900">{selectedRecord.source_of_income}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">New Families Info</p>
                          <p className="font-medium text-gray-900">{selectedRecord.new_families_info || 'N/A'}</p>
                        </div>
                      </div>
                      {selectedRecord.siblings?.length > 0 && (
                        <div className="mt-4">
                          <p className="text-sm text-gray-600">Siblings</p>
                          <ul className="list-disc pl-5">
                            {selectedRecord.siblings.map((sibling, index) => (
                              <li key={index} className="font-medium text-gray-900">
                                {sibling.name} (Age: {sibling.age}, Class: {sibling.class})
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {selectedRecord.other_family_members?.length > 0 && (
                        <div className="mt-4">
                          <p className="text-sm text-gray-600">Other Family Members</p>
                          <ul className="list-disc pl-5">
                            {selectedRecord.other_family_members.map((member, index) => (
                              <li key={index} className="font-medium text-gray-900">{member.name}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    <div className="bg-purple-50 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Application Status</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600">Current Status</p>
                          <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mt-2 ${getStatusColor(selectedRecord.status)}`}>
                            {selectedRecord.status}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Submitted Date</p>
                          <p className="font-medium text-gray-900">{selectedRecord.created_at.split('T')[0]}</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-amber-50 rounded-lg p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Economic and Housing</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600">House Condition</p>
                          <p className="font-medium text-gray-900">{selectedRecord.house_condition}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Main Reasons for Sponsorship</p>
                          <p className="font-medium text-gray-900">{selectedRecord.main_reasons}</p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
                >
                  Close
                </button>
                {isEditing ? (
                  <button
                    onClick={handleSaveEdit}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium flex items-center gap-2"
                  >
                    <Save size={20} />
                    Save Changes
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => handleEdit(selectedRecord)}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                    >
                      Edit Record
                    </button>
                    <button
                      onClick={handleExportPDF}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-medium flex items-center gap-2"
                    >
                      <Download size={20} />
                      Export as PDF
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}